import { z } from 'zod';
import { apiClient } from '@/api/client';

/**
 * SU-1 (W7): organisation lifecycle and « ouvrir en tant que », super admin
 * only (server/src/adapters/inbound/http/admin/organisations.routes.ts,
 * ADR-050). Also wires the routes that had no caller in src/:
 * POST /admin/bulk-delete-clients, /admin/send-onboarding-emails,
 * DELETE /admin/users/:id and POST /admin/organisations/:id/users.
 */

export type OrganisationStatus = 'active' | 'disabled' | 'deleted';

const LifecycleSchema = z.object({
  organisation: z.object({
    id: z.string(),
    company_name: z.string(),
    contact_name: z.string(),
    status: z.enum(['active', 'disabled', 'deleted']),
  }).passthrough(),
});
export type LifecycleResult = z.infer<typeof LifecycleSchema>['organisation'];

const enc = encodeURIComponent;

export async function updateOrganisation(id: string, patch: { company_name?: string; contact_name?: string }): Promise<LifecycleResult> {
  return LifecycleSchema.parse(await apiClient.patch<unknown>(`/admin/organisations/${enc(id)}`, patch)).organisation;
}

export async function deactivateOrganisation(id: string): Promise<LifecycleResult> {
  return LifecycleSchema.parse(await apiClient.post<unknown>(`/admin/organisations/${enc(id)}/deactivate`)).organisation;
}

export async function reactivateOrganisation(id: string): Promise<LifecycleResult> {
  return LifecycleSchema.parse(await apiClient.post<unknown>(`/admin/organisations/${enc(id)}/reactivate`)).organisation;
}

/** Soft delete; the server checks `confirm_name` too (ENG-17). */
export async function softDeleteOrganisation(id: string, confirmName: string): Promise<LifecycleResult> {
  return LifecycleSchema.parse(await apiClient.delete<unknown>(`/admin/organisations/${enc(id)}`, { body: { confirm_name: confirmName } })).organisation;
}

/**
 * Permanent purge of several organisations (POST /admin/bulk-delete-clients).
 * The phone numbers are carrier resources the purge never deletes: the server
 * counts them per organisation (`purged[id].numbers_left`); `numbersLeft` is
 * their sum, so the page can say they still have to be released (REV-W7-F5).
 */
export async function purgeOrganisations(ids: string[], confirmName: string): Promise<{ deleted: number; numbersLeft: number }> {
  const raw = await apiClient.post<{ deleted: number; purged?: Record<string, { numbers_left?: number }> }>(
    '/admin/bulk-delete-clients', { clientIds: ids, confirm_name: confirmName },
  );
  const numbersLeft = Object.values(raw?.purged ?? {}).reduce((n, p) => n + (Number(p?.numbers_left) || 0), 0);
  return { deleted: Number(raw?.deleted ?? 0), numbersLeft };
}

/** What the server asks to type for a purge: one name, or « <n> organisations » (same in fr and en). */
export function purgeConfirmPhrase(names: string[]): string {
  return names.length === 1 ? names[0]! : `${names.length} organisations`;
}

/** Despite its path, an in-app welcome notification, never an email (AUDIT-ADM-19). */
export async function sendWelcomeNotification(emails: string[]): Promise<{ notified: number }> {
  const raw = await apiClient.post<{ results?: Array<{ notified: boolean }> }>('/admin/send-onboarding-emails', { clientEmails: emails });
  return { notified: (raw?.results ?? []).filter((r) => r.notified).length };
}

const DetailSchema = z.object({
  members: z.array(z.object({
    membership_id: z.string(),
    user_id: z.string(),
    email: z.string(),
    full_name: z.string(),
    role: z.enum(['tenant_admin', 'caller', 'viewer']),
    is_active: z.boolean(),
  }).passthrough()),
});
export type OrganisationMember = z.infer<typeof DetailSchema>['members'][number];

export async function listOrganisationMembers(id: string, options?: { signal?: AbortSignal }): Promise<OrganisationMember[]> {
  return DetailSchema.parse(await apiClient.get<unknown>(`/admin/organisations/${enc(id)}`, options)).members;
}

export interface AddMemberInput { email: string; fullName?: string; role: 'tenant_admin' | 'caller' | 'viewer' }

/** No password: the member is invited by email (W7). */
export async function addOrganisationMember(id: string, input: AddMemberInput): Promise<{ invited: boolean; email_sent: boolean }> {
  const raw = await apiClient.post<{ invited?: boolean; email_sent?: boolean }>(`/admin/organisations/${enc(id)}/users`, input);
  return { invited: raw?.invited === true, email_sent: raw?.email_sent === true };
}

/** Deletes the account and its memberships on every organisation (calls kept). */
export async function deleteUserAccount(userId: string): Promise<void> {
  await apiClient.delete<unknown>(`/admin/users/${enc(userId)}`);
}

const ImpersonationSchema = z.object({
  impersonation: z.object({
    subject_organisation_id: z.string(),
    subject_user_id: z.string(),
    subject_email: z.string(),
    role: z.enum(['tenant_admin', 'caller', 'viewer']),
    impersonation_expires_at: z.string(),
  }),
});
export type ImpersonationStarted = z.infer<typeof ImpersonationSchema>['impersonation'];

/** « Ouvrir en tant que »: read-only, 30 minutes (ENG-10). Without userId the server picks the oldest admin. */
export async function startImpersonation(organisationId: string, userId?: string): Promise<ImpersonationStarted> {
  const raw = await apiClient.post<unknown>(`/admin/organisations/${enc(organisationId)}/impersonate`, userId ? { user_id: userId } : {});
  return ImpersonationSchema.parse(raw).impersonation;
}

export async function stopImpersonation(): Promise<void> {
  await apiClient.post<unknown>('/auth/impersonation/stop');
}
